package org.gymly.i18n

import org.gymly.api.GYMLY_API_PORT

/**
 * Alertas y errores de usuario (API, red, auth, fallos genéricos).
 * Usado desde UI Compose y desde [org.gymly.util.userMessage] vía [ActiveLocale].
 */
interface AlertStrings {
    val invalidCredentials: String
    val unexpectedServerError: String
    val accessDenied: String
    val sessionInvalid: String
    val connectionProxy: String
    val connectionTimeout: String
    val connectionTimeoutAdb: String
    val apiNotFound: String
    val apiBadGateway: String
    val apiHtmlResponse: String
    val noConnectionOrServer: String
    val loginFailed: String
    val saveFailed: String
    val loadFailed: String
    val deleteFailed: String
    val createFailed: String
    val updateFailed: String
    val cancelFailed: String
    val genericError: String

    val visitAlreadyRegisteredClient: String
    val visitAlreadyRegisteredStaff: String

    /** Traduce códigos `error.*` y textos conocidos al idioma activo. */
    fun localize(raw: String): String {
        val n = raw.trim()
        if (n.isEmpty()) return n
        codeMessage(n)?.let { return it }
        phraseMessage(n)?.let { return it }
        return n
    }

    private fun codeMessage(code: String): String? = when (code) {
        "error.auth.invalid_credentials" -> invalidCredentials
        "error.server.unexpected" -> unexpectedServerError
        "error.auth.forbidden" -> accessDenied
        "error.auth.unauthorized" -> sessionInvalid
        "error.net.proxy" -> connectionProxy
        "error.net.timeout" -> connectionTimeout
        "error.net.timeout_adb" -> connectionTimeoutAdb
        "error.net.api_not_found" -> apiNotFound
        "error.net.bad_gateway" -> apiBadGateway
        "error.net.html_response" -> apiHtmlResponse
        "error.visit.already_registered_client" -> visitAlreadyRegisteredClient
        "error.visit.already_registered_staff" -> visitAlreadyRegisteredStaff
        else -> null
    }

    private fun phraseMessage(n: String): String? {
        val lower = n.lowercase()
        return when {
            lower == "invalid credentials" ||
                n.equals("Correo o contraseña incorrectos.", ignoreCase = true) ->
                invalidCredentials

            lower == "unexpected error" ||
                n.startsWith("Error interno del servidor") ||
                n.startsWith("Internal server error") ->
                unexpectedServerError

            n.contains("Acceso denegado", ignoreCase = true) ||
                n.contains("Access denied", ignoreCase = true) ->
                accessDenied

            n.contains("Sesión no válida", ignoreCase = true) ||
                n.contains("Session is not valid", ignoreCase = true) ||
                n.contains("Invalid session", ignoreCase = true) ->
                sessionInvalid

            n.contains("trying to proxy", ignoreCase = true) ||
                n.contains("ECONNREFUSED", ignoreCase = true) ||
                n.contains("proxy error", ignoreCase = true) ||
                n.contains("No se pudo conectar con el backend", ignoreCase = true) ||
                n.contains("Could not connect to the backend", ignoreCase = true) ->
                connectionProxy

            n.contains("timeout", ignoreCase = true) && n.contains("adb reverse", ignoreCase = true) ->
                connectionTimeoutAdb

            n.contains("timeout", ignoreCase = true) ||
                n.contains("Fail to fetch", ignoreCase = true) ||
                n.contains("Failed to fetch", ignoreCase = true) ||
                n.contains("No se pudo conectar al API", ignoreCase = true) ||
                n.contains("Could not connect to the API", ignoreCase = true) ->
                connectionTimeout

            n.contains("Cannot POST", ignoreCase = true) ||
                n.contains("Cannot GET", ignoreCase = true) ||
                n.contains("No se encontró el API", ignoreCase = true) ||
                n.contains("API was not found", ignoreCase = true) ->
                apiNotFound

            n.contains("502") || n.contains("Bad Gateway", ignoreCase = true) ||
                n.contains("El API no responde", ignoreCase = true) ||
                n.contains("API is not responding", ignoreCase = true) ->
                apiBadGateway

            n.contains("respondió HTML", ignoreCase = true) ||
                n.contains("responded with HTML", ignoreCase = true) ->
                apiHtmlResponse

            n.contains("sesión de este cliente", ignoreCase = true) ||
                n.contains("client already has a session", ignoreCase = true) ->
                visitAlreadyRegisteredClient

            n.contains("sesión de este trabajador", ignoreCase = true) ||
                n.contains("worker already has a session", ignoreCase = true) ||
                n.contains("staff already has a session", ignoreCase = true) ->
                visitAlreadyRegisteredStaff

            // Fallbacks frecuentes en catch blocks (ES → idioma activo)
            n.equals("No se pudo guardar.", ignoreCase = true) ||
                n.equals("No se pudo guardar", ignoreCase = true) ->
                saveFailed

            n.equals("No se pudo eliminar.", ignoreCase = true) ||
                n.equals("No se pudo eliminar", ignoreCase = true) ->
                deleteFailed

            n.equals("No se pudo actualizar.", ignoreCase = true) ||
                n.equals("No se pudo actualizar", ignoreCase = true) ->
                updateFailed

            n.equals("No se pudo cancelar.", ignoreCase = true) ||
                n.equals("No se pudo cancelar", ignoreCase = true) ->
                cancelFailed

            n.equals("No se pudo iniciar sesión.", ignoreCase = true) ->
                loginFailed

            n.equals("Sin conexión o error del servidor.", ignoreCase = true) ->
                noConnectionOrServer

            n.equals("Error", ignoreCase = true) ||
                n.equals("Ocurrió un error.", ignoreCase = true) ->
                genericError

            else -> exactFallback(n)
        }
    }

    private fun exactFallback(n: String): String? = FALLBACKS[n.trim()]?.let { (es, en) ->
        if (ActiveLocale.current == GymlyLocale.EN) en else es
    }

    companion object {
        /** Fallbacks frecuentes de catch blocks: se muestran en el idioma activo. */
        private val FALLBACKS: Map<String, Pair<String, String>> = mapOf(
            "No se pudieron cargar las sesiones." to
                ("No se pudieron cargar las sesiones." to "Could not load the sessions."),
            "No se pudo marcar la asistencia." to
                ("No se pudo marcar la asistencia." to "Could not mark attendance."),
            "No se pudo marcar la no asistencia." to
                ("No se pudo marcar la no asistencia." to "Could not mark as no-show."),
            "No se pudo crear la reserva." to
                ("No se pudo crear la reserva." to "Could not create the booking."),
            "No se pudo guardar la reserva." to
                ("No se pudo guardar la reserva." to "Could not save the booking."),
            "No se pudo eliminar la reserva." to
                ("No se pudo eliminar la reserva." to "Could not delete the booking."),
            "No se pudo eliminar la sesión." to
                ("No se pudo eliminar la sesión." to "Could not delete the session."),
            "No se pudo registrar la sesión." to
                ("No se pudo registrar la sesión." to "Could not register the session."),
            "No se pudieron cargar los horarios." to
                ("No se pudieron cargar los horarios." to "Could not load the schedules."),
            "No se pudo crear el horario." to
                ("No se pudo crear el horario." to "Could not create the schedule."),
            "No se pudo guardar el horario." to
                ("No se pudo guardar el horario." to "Could not save the schedule."),
            "No se pudo eliminar el horario." to
                ("No se pudo eliminar el horario." to "Could not delete the schedule."),
            "No se pudo duplicar la semana." to
                ("No se pudo duplicar la semana." to "Could not duplicate the week."),
            "No se pudieron cargar los pagos." to
                ("No se pudieron cargar los pagos." to "Could not load the payments."),
            "No se pudo registrar el pago." to
                ("No se pudo registrar el pago." to "Could not register the payment."),
            "No se pudo guardar el pago." to
                ("No se pudo guardar el pago." to "Could not save the payment."),
            "No se pudo eliminar el pago." to
                ("No se pudo eliminar el pago." to "Could not delete the payment."),
            "No se pudo confirmar el pago." to
                ("No se pudo confirmar el pago." to "Could not confirm the payment."),
            "No se pudieron cargar las ventas." to
                ("No se pudieron cargar las ventas." to "Could not load the sales."),
            "No se pudo cobrar la venta." to
                ("No se pudo cobrar la venta." to "Could not charge the sale."),
            "No se pudo cancelar la venta." to
                ("No se pudo cancelar la venta." to "Could not cancel the sale."),
            "No se pudo registrar la venta." to
                ("No se pudo registrar la venta." to "Could not register the sale."),
            "No se pudo guardar la venta." to
                ("No se pudo guardar la venta." to "Could not save the sale."),
            "No se pudo eliminar la venta." to
                ("No se pudo eliminar la venta." to "Could not delete the sale."),
            "No se pudo descargar la factura." to
                ("No se pudo descargar la factura." to "Could not download the invoice."),
            "No se pudieron cargar las promociones." to
                ("No se pudieron cargar las promociones." to "Could not load the promotions."),
            "No se pudo crear la promoción." to
                ("No se pudo crear la promoción." to "Could not create the promotion."),
            "No se pudo guardar la promoción." to
                ("No se pudo guardar la promoción." to "Could not save the promotion."),
            "No se pudo eliminar la promoción." to
                ("No se pudo eliminar la promoción." to "Could not delete the promotion."),
            "No se pudieron cargar los trabajadores." to
                ("No se pudieron cargar los trabajadores." to "Could not load the staff."),
            "No se pudo guardar el trabajador." to
                ("No se pudo guardar el trabajador." to "Could not save the staff member."),
            "No se pudo eliminar el trabajador." to
                ("No se pudo eliminar el trabajador." to "Could not delete the staff member."),
            "No se pudieron cargar los usuarios." to
                ("No se pudieron cargar los usuarios." to "Could not load the users."),
            "No se pudo crear el usuario." to
                ("No se pudo crear el usuario." to "Could not create the user."),
            "No se pudo guardar el usuario." to
                ("No se pudo guardar el usuario." to "Could not save the user."),
            "No se pudo eliminar el usuario." to
                ("No se pudo eliminar el usuario." to "Could not delete the user."),
            "No se pudieron cargar los roles." to
                ("No se pudieron cargar los roles." to "Could not load the roles."),
            "No se pudo crear el rol." to
                ("No se pudo crear el rol." to "Could not create the role."),
            "No se pudo guardar el rol." to
                ("No se pudo guardar el rol." to "Could not save the role."),
            "No se pudo eliminar el rol." to
                ("No se pudo eliminar el rol." to "Could not delete the role."),
            "No se pudo cargar el reporte." to
                ("No se pudo cargar el reporte." to "Could not load the report."),
            "No se pudo exportar el reporte." to
                ("No se pudo exportar el reporte." to "Could not export the report."),
            "No se pudo cargar el perfil." to
                ("No se pudo cargar el perfil." to "Could not load the profile."),
            "No se pudo guardar el perfil." to
                ("No se pudo guardar el perfil." to "Could not save the profile."),
            "No se pudo cambiar la contraseña." to
                ("No se pudo cambiar la contraseña." to "Could not change the password."),
            "No se pudo eliminar la foto." to
                ("No se pudo eliminar la foto." to "Could not delete the photo."),
            "No se pudo subir la foto." to
                ("No se pudo subir la foto." to "Could not upload the photo."),
            "No se pudieron guardar las preferencias." to
                ("No se pudieron guardar las preferencias." to "Could not save preferences."),
            "No se pudo cargar WhatsApp." to
                ("No se pudo cargar WhatsApp." to "Could not load WhatsApp."),
            "No se pudo cargar el acceso." to
                ("No se pudo cargar el acceso." to "Could not load access settings."),
            "No se pudo cargar la empresa." to
                ("No se pudo cargar la empresa." to "Could not load the company."),
            "No se pudo cargar la cotización." to
                ("No se pudo cargar la cotización." to "Could not load the exchange rate."),
            "No se pudo cargar la auditoría." to
                ("No se pudo cargar la auditoría." to "Could not load the audit log."),
            "No se pudieron cargar las órdenes de membresía." to
                ("No se pudieron cargar las órdenes de membresía." to "Could not load membership orders."),
            "No se pudo crear la orden." to
                ("No se pudo crear la orden." to "Could not create the order."),
            "No se pudo cargar el monedero digital" to
                ("No se pudo cargar el monedero digital." to "Could not load the digital wallet."),
            "No se pudo cargar el monedero digital." to
                ("No se pudo cargar el monedero digital." to "Could not load the digital wallet."),
            "No se pudo aplicar el saldo" to
                ("No se pudo aplicar el saldo." to "Could not apply the credit."),
            "No se pudo aplicar el saldo." to
                ("No se pudo aplicar el saldo." to "Could not apply the credit."),
            "Error al cargar reservas." to
                ("Error al cargar reservas." to "Could not load bookings."),
            "Error al cargar horarios." to
                ("Error al cargar horarios." to "Could not load schedules."),
            "Error al cargar pagos." to
                ("Error al cargar pagos." to "Could not load payments."),
            "Error al cargar la tienda." to
                ("Error al cargar la tienda." to "Could not load the store."),
            "No se pudo reservar." to
                ("No se pudo reservar." to "Could not book."),
            "No se pudo confirmar el pedido." to
                ("No se pudo confirmar el pedido." to "Could not confirm the order."),
            "Selecciona un cliente." to
                ("Selecciona un cliente." to "Select a client."),
            "Selecciona una franja." to
                ("Selecciona una franja." to "Select a time slot."),
            "Selecciona un trabajador." to
                ("Selecciona un trabajador." to "Select a worker."),
            "No se pudieron cargar las órdenes." to
                ("No se pudieron cargar las órdenes." to "Could not load the orders."),
            "No se pudo guardar la orden." to
                ("No se pudo guardar la orden." to "Could not save the order."),
            "No se pudo eliminar la orden." to
                ("No se pudo eliminar la orden." to "Could not delete the order."),
            "No se pudieron cargar los planes." to
                ("No se pudieron cargar los planes." to "Could not load the plans."),
            "No se pudo guardar el plan." to
                ("No se pudo guardar el plan." to "Could not save the plan."),
            "No se pudo eliminar el plan." to
                ("No se pudo eliminar el plan." to "Could not delete the plan."),
            "No se pudo cargar la configuración de pagos." to
                ("No se pudo cargar la configuración de pagos." to "Could not load payment settings."),
            "No se pudo cargar la configuración de correo." to
                ("No se pudo cargar la configuración de correo." to "Could not load mail settings."),
            "No se pudieron cargar las reglas de notificación." to
                ("No se pudieron cargar las reglas de notificación." to "Could not load notification rules."),
            "No se pudieron guardar los horarios." to
                ("No se pudieron guardar los horarios." to "Could not save the schedules."),
            "No se pudo guardar la plantilla." to
                ("No se pudo guardar la plantilla." to "Could not save the template."),
            "No se pudo restaurar la plantilla." to
                ("No se pudo restaurar la plantilla." to "Could not restore the template."),
            "No se pudo abrir el comprobante." to
                ("No se pudo abrir el comprobante." to "Could not open the receipt."),
            "No se pudo descargar el recibo." to
                ("No se pudo descargar el recibo." to "Could not download the receipt."),
            "No se pudo enviar el recibo." to
                ("No se pudo enviar el recibo." to "Could not send the receipt."),
            "Error al registrar cobro" to
                ("Error al registrar cobro." to "Could not register the charge."),
            "Error al registrar cobros combinados" to
                ("Error al registrar cobros combinados." to "Could not register combined charges."),
            "No se pudieron cargar planes/entrenadores." to
                ("No se pudieron cargar planes/entrenadores." to "Could not load plans/trainers."),
            "No se pudieron cargar los terminales." to
                ("No se pudieron cargar los terminales." to "Could not load the terminals."),
            "No se pudo capturar la foto en el terminal." to
                ("No se pudo capturar la foto en el terminal." to "Could not capture the photo on the terminal."),
            "No se pudieron cargar los tipos de pago." to
                ("No se pudieron cargar los tipos de pago." to "Could not load payment types."),
            "No se pudo crear el tipo de pago." to
                ("No se pudo crear el tipo de pago." to "Could not create the payment type."),
            "No se pudo ejecutar el recordatorio." to
                ("No se pudo ejecutar el recordatorio." to "Could not run the reminder."),
            "No se pudo cargar el detalle del monedero" to
                ("No se pudo cargar el detalle del monedero." to "Could not load wallet details."),
            "No se pudo cargar el detalle" to
                ("No se pudo cargar el detalle." to "Could not load the details."),
        )
    }
}

internal object AlertEs : AlertStrings {
    override val invalidCredentials = "Correo o contraseña incorrectos."
    override val unexpectedServerError = "Error interno del servidor. Revisa el log del backend."
    override val accessDenied = "Acceso denegado. Cierra sesión y vuelve a entrar con tu cuenta de socio."
    override val sessionInvalid = "Sesión no válida. Vuelve a iniciar sesión."
    override val connectionProxy =
        "No se pudo conectar con el backend (puerto $GYMLY_API_PORT). " +
            "Arranca el servidor: docker compose --profile dev up -d backend-dev"
    override val connectionTimeout =
        "No se pudo conectar al API (puerto $GYMLY_API_PORT). Arranca el backend: docker compose --profile dev up -d"
    override val connectionTimeoutAdb =
        "No se pudo conectar al API. ¿Está el backend en marcha (puerto $GYMLY_API_PORT)? En emulador ejecuta: adb reverse tcp:$GYMLY_API_PORT tcp:$GYMLY_API_PORT"
    override val apiNotFound =
        "No se encontró el API en ese puerto. Arranca el backend Spring (puerto $GYMLY_API_PORT) " +
            "con docker compose --profile dev up -d."
    override val apiBadGateway =
        "El API no responde (502). En el servidor revisa: docker compose ps y logs del contenedor backend."
    override val apiHtmlResponse = "El servidor respondió HTML en lugar del API JSON. ¿Está el backend Spring en marcha?"
    override val noConnectionOrServer = "Sin conexión o error del servidor."
    override val loginFailed = "No se pudo iniciar sesión."
    override val saveFailed = "No se pudo guardar."
    override val loadFailed = "No se pudo cargar."
    override val deleteFailed = "No se pudo eliminar."
    override val createFailed = "No se pudo crear."
    override val updateFailed = "No se pudo actualizar."
    override val cancelFailed = "No se pudo cancelar."
    override val genericError = "Ocurrió un error."
    override val visitAlreadyRegisteredClient = "Ya se registró la sesión de este cliente hoy."
    override val visitAlreadyRegisteredStaff = "Ya se registró la sesión de este trabajador hoy."
}

internal object AlertEn : AlertStrings {
    override val invalidCredentials = "Incorrect email or password."
    override val unexpectedServerError = "Internal server error. Check the backend log."
    override val accessDenied = "Access denied. Sign out and sign back in with your member account."
    override val sessionInvalid = "Session is not valid. Please sign in again."
    override val connectionProxy =
        "Could not connect to the backend (port $GYMLY_API_PORT). " +
            "Start the server: docker compose --profile dev up -d backend-dev"
    override val connectionTimeout =
        "Could not connect to the API (port $GYMLY_API_PORT). Start the backend: docker compose --profile dev up -d"
    override val connectionTimeoutAdb =
        "Could not connect to the API. Is the backend running (port $GYMLY_API_PORT)? On an emulator run: adb reverse tcp:$GYMLY_API_PORT tcp:$GYMLY_API_PORT"
    override val apiNotFound =
        "API was not found on that port. Start the Spring backend (port $GYMLY_API_PORT) " +
            "with docker compose --profile dev up -d."
    override val apiBadGateway =
        "The API is not responding (502). On the server check: docker compose ps and backend container logs."
    override val apiHtmlResponse = "The server responded with HTML instead of API JSON. Is the Spring backend running?"
    override val noConnectionOrServer = "No connection or server error."
    override val loginFailed = "Could not sign in."
    override val saveFailed = "Could not save."
    override val loadFailed = "Could not load."
    override val deleteFailed = "Could not delete."
    override val createFailed = "Could not create."
    override val updateFailed = "Could not update."
    override val cancelFailed = "Could not cancel."
    override val genericError = "Something went wrong."
    override val visitAlreadyRegisteredClient = "This client already has a session registered today."
    override val visitAlreadyRegisteredStaff = "This worker already has a session registered today."
}

/** Idioma activo fuera de Compose (p. ej. [org.gymly.util.userMessage]). */
object ActiveLocale {
    var current: GymlyLocale = GymlyLocale.ES

    fun setFromPreferred(value: String?) {
        current = GymlyLocale.from(value)
    }
}

fun alertStrings(locale: GymlyLocale = ActiveLocale.current): AlertStrings =
    when (locale) {
        GymlyLocale.ES -> AlertEs
        GymlyLocale.EN -> AlertEn
    }
